import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSend, FiRefreshCw, FiImage, FiBell, FiX, FiCheck } from 'react-icons/fi';
import { API_URL, BASE_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './NotificationManagement.css';

const imgUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const p = path.replace(/^\/+/, '');
  return `${BASE_URL}/${p.startsWith('uploads/') ? p : `uploads/${p}`}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const NotificationManagement = () => {
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [notifSettings, setNotifSettings] = useState({
    all_users: false,
    category_selected_users: true,
  });
  const [settingsSaving, setSettingsSaving] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const fetchNotificationSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/notification-settings`, { headers: headers() });
      setNotifSettings({
        all_users: !!res.data?.all_users,
        category_selected_users: !!res.data?.category_selected_users,
      });
    } catch (e) {
      console.error('Notification settings fetch failed', e);
    }
  }, []);

  const saveNotificationSettings = async (next) => {
    // Optimistic update — flip UI immediately
    const prev = notifSettings;
    setNotifSettings(next);
    setSettingsSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/notification-settings`, next, { headers: headers() });
      // Confirm with server-returned values
      setNotifSettings({
        all_users: !!res.data?.all_users,
        category_selected_users: !!res.data?.category_selected_users,
      });
      toast.success('Notification settings saved');
    } catch (err) {
      // Roll back on failure
      setNotifSettings(prev);
      toast.error(err.response?.data?.error || 'Failed to save notification settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleToggleAllUsers = async () => {
    if (notifSettings.all_users || settingsSaving) return;
    await saveNotificationSettings({ all_users: true, category_selected_users: false });
  };

  const handleToggleCategoryUsers = async () => {
    if (notifSettings.category_selected_users || settingsSaving) return;
    await saveNotificationSettings({ all_users: false, category_selected_users: true });
  };

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/notification-management`, { headers: headers() });
      setHistory(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      console.error('Notification history fetch failed', e);
      toast.error('Failed to load notification history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchNotificationSettings();
  }, [fetchHistory, fetchNotificationSettings]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/upload/single`, form, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      const filename = res.data?.filename || res.data?.path?.split('/').pop();
      if (!filename) throw new Error('Upload failed');
      setImage(filename);
      setImagePreview(imgUrl(filename));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImage('');
    setImagePreview('');
  };

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error('Content is required');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/admin/notification-management/send`,
        { content: trimmed, image: image || '' },
        { headers: headers() }
      );
      toast.success('Notification sent to web and mobile users');
      setContent('');
      setImage('');
      setImagePreview('');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const openDetail = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/admin/notification-management/${item.id}`, {
        headers: headers(),
      });
      setSelected(res.data);
    } catch {
      setSelected(item);
    }
  };

  return (
    <div className="admin-page notif-mgmt">
      <div className="page-header notif-mgmt__header">
        <div>
          <h1>Notification Management</h1>
          <p>Send announcements to web and mobile app users. View delivery history and status.</p>
        </div>
        <button type="button" className="notif-mgmt__refresh" onClick={fetchHistory}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="notif-mgmt__grid">
        <section className="admin-card notif-mgmt__composer">
          <div className="notif-mgmt__composer-head">
            <FiBell />
            <h2>Send Notification</h2>
          </div>

          <label className="notif-mgmt__label">
            Content <span className="notif-mgmt__required">*</span>
          </label>
          <textarea
            className="notif-mgmt__textarea"
            rows={6}
            placeholder="Write your announcement message..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          <label className="notif-mgmt__label">Image (Optional)</label>
          <div className="notif-mgmt__image-row">
            <label className="notif-mgmt__upload-btn">
              <FiImage />
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input type="file" accept="image/*" hidden onChange={handleImageUpload} disabled={uploading} />
            </label>
            {imagePreview && (
              <button type="button" className="notif-mgmt__remove-img" onClick={handleRemoveImage}>
                Remove
              </button>
            )}
          </div>
          {imagePreview && (
            <div className="notif-mgmt__preview">
              <img src={imagePreview} alt="Notification preview" />
            </div>
          )}

          <button
            type="button"
            className="notif-mgmt__send-btn"
            onClick={handleSend}
            disabled={sending || !content.trim()}
          >
            <FiSend />
            {sending ? 'Sending...' : 'Send Notification'}
          </button>
        </section>

        <section className="admin-card notif-mgmt__stats-card">
          <h2>Notification Settings</h2>
          <p className="notif-mgmt__settings-desc">
            Control who receives status-change notifications when a property is approved.
          </p>
          <div className="notif-mgmt__settings-list">
            <label className="notif-mgmt__setting-row">
              <span>
                <strong>All Users</strong>
                <small>Send to every registered user</small>
              </span>
              <button
                type="button"
                className={`notif-mgmt__toggle ${notifSettings.all_users ? 'is-on' : ''}`}
                onClick={handleToggleAllUsers}
                disabled={settingsSaving}
                aria-pressed={notifSettings.all_users}
              >
                <span className="notif-mgmt__toggle-knob" />
              </button>
            </label>
            <label className="notif-mgmt__setting-row">
              <span>
                <strong>Category Selected Users</strong>
                <small>Only users with saved category and city matching the listing</small>
              </span>
              <button
                type="button"
                className={`notif-mgmt__toggle ${notifSettings.category_selected_users ? 'is-on' : ''}`}
                onClick={handleToggleCategoryUsers}
                disabled={settingsSaving}
                aria-pressed={notifSettings.category_selected_users}
              >
                <span className="notif-mgmt__toggle-knob" />
              </button>
            </label>
          </div>
        </section>

        <section className="admin-card notif-mgmt__stats-card">
          <h2>Delivery Overview</h2>
          <div className="notif-mgmt__stats">
            <div className="notif-mgmt__stat">
              <span className="notif-mgmt__stat-num">{total}</span>
              <span className="notif-mgmt__stat-label">Total Campaigns</span>
            </div>
            <div className="notif-mgmt__stat">
              <span className="notif-mgmt__stat-num">
                {history.reduce((sum, h) => sum + (h.in_app_delivered || 0), 0)}
              </span>
              <span className="notif-mgmt__stat-label">In-App Delivered</span>
            </div>
            <div className="notif-mgmt__stat">
              <span className="notif-mgmt__stat-num">
                {history.reduce((sum, h) => sum + (h.web_delivered || 0), 0)}
              </span>
              <span className="notif-mgmt__stat-label">Web Push Reach</span>
            </div>
            <div className="notif-mgmt__stat">
              <span className="notif-mgmt__stat-num">
                {history.reduce((sum, h) => sum + (h.mobile_delivered || 0), 0)}
              </span>
              <span className="notif-mgmt__stat-label">Mobile Push Reach</span>
            </div>
          </div>
          <p className="notif-mgmt__note">
            Broadcast notifications are delivered to all registered users and partners.
            Status-change notifications follow the Notification Settings above.
          </p>
        </section>
      </div>

      <section className="admin-card notif-mgmt__history">
        <h2>Notification History</h2>
        {loading ? (
          <p className="notif-mgmt__loading">Loading history...</p>
        ) : history.length === 0 ? (
          <p className="notif-mgmt__empty">No notifications sent yet.</p>
        ) : (
          <div className="notif-mgmt__table-wrap">
            <table className="notif-mgmt__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Content</th>
                  <th>Recipients</th>
                  <th>Web</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Sent By</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td className="notif-mgmt__content-cell">
                      {item.image && (
                        <img src={imgUrl(item.image)} alt="" className="notif-mgmt__thumb" />
                      )}
                      <span>{item.content}</span>
                    </td>
                    <td>{item.total_recipients ?? 0}</td>
                    <td>{item.web_delivered ?? 0}</td>
                    <td>{item.mobile_delivered ?? 0}</td>
                    <td>
                      <span className={`notif-mgmt__status notif-mgmt__status--${item.status || 'completed'}`}>
                        {item.status || 'completed'}
                      </span>
                    </td>
                    <td>{item.sent_by_name || 'Admin'}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <button type="button" className="notif-mgmt__view-btn" onClick={() => openDetail(item)}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selected && (
        <div className="notif-mgmt__modal-backdrop" onClick={() => setSelected(null)}>
          <div className="notif-mgmt__modal" onClick={(e) => e.stopPropagation()}>
            <div className="notif-mgmt__modal-head">
              <h3>Notification #{selected.id}</h3>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close"><FiX /></button>
            </div>
            <div className="notif-mgmt__modal-body">
              <p><strong>Content:</strong> {selected.content}</p>
              <p><strong>Status:</strong> {selected.status}</p>
              <p><strong>Recipients:</strong> {selected.total_recipients}</p>
              <p><strong>In-App:</strong> {selected.in_app_delivered} · <strong>Web Push:</strong> {selected.web_delivered} · <strong>Mobile:</strong> {selected.mobile_delivered}</p>
              {selected.image && (
                <img src={imgUrl(selected.image)} alt="" className="notif-mgmt__modal-img" />
              )}
              {selected.delivery_log?.length > 0 && (
                <div className="notif-mgmt__log-wrap">
                  <h4>Delivery Log ({selected.delivery_log.length})</h4>
                  <table className="notif-mgmt__log-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>ID</th>
                        <th>In-App</th>
                        <th>Web</th>
                        <th>Mobile</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.delivery_log.slice(0, 100).map((log, i) => (
                        <tr key={`${log.recipient_type}-${log.recipient_id}-${i}`}>
                          <td>{log.recipient_type}</td>
                          <td>{log.recipient_id}</td>
                          <td>{log.in_app ? <FiCheck size={14} /> : '—'}</td>
                          <td>{log.web_push ? <FiCheck size={14} /> : '—'}</td>
                          <td>{log.mobile_push ? <FiCheck size={14} /> : '—'}</td>
                          <td>{log.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selected.delivery_log.length > 100 && (
                    <p className="notif-mgmt__log-more">Showing first 100 of {selected.delivery_log.length} entries.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
